import './MarginRows.css';
import type { ComponentChildren } from 'preact';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { t } from '../../copy/strings';
import { fmtPoints, fmtVoters, fmtVotesLong } from '../../format/numbers';
import type { HeadlineState, ResultsView } from '../../model/types';

export interface MarginRowsProps {
  /** |shares[1] − shares[0]| × 100 (`HeroModel.marginPts`), em pontos. */
  marginPts: number;
  /** |votes[1] − votes[0]| (`HeroModel.diffVotes`). */
  diffVotes: number;
  /** Eleitores (não votos) em seções não totalizadas (`PlaceView.remainingElectorate`). */
  remainingElectorate: number;
  /** `status === 'complete'`: "Por apurar" diz "Nenhum". */
  complete?: boolean;
  /** `mathDecision` ou `tseResult` (`HeroModel.decided !== null`): "Diferença" no lugar de "Vantagem". */
  decided?: boolean;
  /** Manchete (§13.2): em `leading` a linha 1 traz só os pontos (os votos já estão na manchete). */
  headline?: HeadlineState;
  /** `rows` no herói; `cells` no duelo de governador (§8.3: só votos na célula 1). */
  variant?: 'rows' | 'cells';
}

/**
 * Props da variante `rows` do herói a partir da vista (§7.7 bloco 3; fórmulas de `HeroModel`, §7.2): margem em pontos
 * e diferença em votos sempre absolutas, eleitores por apurar e o estado da manchete.
 */
export function marginRowsProps(v: ResultsView): MarginRowsProps {
  const n = v.national;
  return {
    marginPts: Math.abs(n.shares[1] - n.shares[0]) * 100,
    diffVotes: Math.abs(n.votes[1] - n.votes[0]),
    remainingElectorate: n.remainingElectorate,
    complete: n.status === 'complete',
    decided: (n.tseResult ?? n.mathDecision) !== null,
    headline: v.headline,
    variant: 'rows',
  };
}

/** `{pontos}` com 2 casas (§7.7 bloco 4): 5,94 → "5,94 pontos"; 1 → "1,00 ponto". */
const pointsText = (pts: number): string => fmtPoints(pts / 100, 2);

/** Separador visual de `margin.value` ("{pontos} · {V}"), tirado do próprio catálogo. */
const separator = (): string => t('margin.value', { pontos: '', V: '' });

/**
 * "Vantagem"/"Diferença" e "Por apurar" (§7.7). Diferença sempre absoluta e sem sinal, sem nome de candidato
 * (AC-7.7-05): quem está à frente é da manchete e da barra. Números por `AnimatedNumber` (cópia `.sr-only` com o valor
 * final); o " · " entre pontos e votos é visual (`aria-hidden`) e a frase acessível usa vírgula (§15.4.3 regra 6).
 * Marcação própria, no padrão do `KeyValueRow` (§3.9): a linha 1 leva dois números animados, que o `value` único do
 * componente base não comporta, e a variante `cells` põe o rótulo acima do valor.
 */
export function MarginRows({
  marginPts,
  diffVotes,
  remainingElectorate,
  complete = false,
  decided = false,
  headline,
  variant = 'rows',
}: MarginRowsProps) {
  const tie = !decided && diffVotes === 0;
  const pointsOnly = variant === 'rows' && !decided && headline === 'leading';
  const votesOnly = variant === 'cells';
  const none = complete || remainingElectorate <= 0;

  let lead: ComponentChildren;
  if (tie) lead = t('margin.empty');
  else if (votesOnly) lead = <AnimatedNumber value={diffVotes} format={fmtVotesLong} />;
  else if (pointsOnly) lead = <AnimatedNumber value={marginPts} format={pointsText} />;
  else {
    lead = (
      <>
        <AnimatedNumber value={marginPts} format={pointsText} />
        <span aria-hidden="true">{separator()}</span>
        <span class="sr-only">, </span>
        <AnimatedNumber value={diffVotes} format={fmtVotesLong} />
      </>
    );
  }

  return (
    <dl class={`margin-rows margin-rows--${variant}`} data-testid="margin-rows">
      <div class="margin-rows__row" data-state={tie ? 'empty' : undefined}>
        <dt class="margin-rows__key">{t(decided ? 'margin.difference' : 'margin.advantage')}</dt>
        <dd class="margin-rows__value">{lead}</dd>
      </div>
      <div class="margin-rows__row">
        <dt class="margin-rows__key">{t('margin.remaining')}</dt>
        <dd class="margin-rows__value">
          {none ? t('margin.none') : <AnimatedNumber value={remainingElectorate} format={fmtVoters} />}
        </dd>
      </div>
    </dl>
  );
}
